if (!customElements.get('product-modal')) {
  customElements.define(
    'product-modal',
    class ProductModal extends ModalDialog {
      constructor() {
        super();
        this.content = this.querySelector('.product-media-modal__content');
        this.mediaItems = Array.from(this.querySelectorAll('.product-media-modal__content > [data-media-id]'));
        this.previousButton = this.querySelector('[data-product-modal-previous]');
        this.nextButton = this.querySelector('[data-product-modal-next]');
        this.currentPageElement = this.querySelector('[data-product-modal-current]');
        this.totalPagesElement = this.querySelector('[data-product-modal-total]');
        this.currentIndex = 0;
        this.zoomLevel = 1;
        this.pan = { x: 0, y: 0 };
        this.pointers = new Map();
        this.lastTap = null;

        this.previousButton?.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();
          this.changeMedia(-1);
        });
        this.nextButton?.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();
          this.changeMedia(1);
        });

        this.content?.addEventListener('pointerdown', this.onPointerDown.bind(this));
        this.content?.addEventListener('pointermove', this.onPointerMove.bind(this));
        this.content?.addEventListener('pointerup', this.onPointerUp.bind(this));
        this.content?.addEventListener('pointercancel', this.onPointerUp.bind(this));
        this.content?.addEventListener('dblclick', this.onDoubleClick.bind(this));
        this.content?.addEventListener('wheel', this.onWheel.bind(this), { passive: false });
        this.addEventListener('click', this.onBackdropClick.bind(this));
        this.addEventListener('keydown', this.onKeydown.bind(this));
      }

      hide() {
        this.resetZoom();
        this.pointers.clear();
        super.hide();
      }

      show(opener) {
        super.show(opener);
        this.showActiveMedia();
      }

      showActiveMedia() {
        if (!this.content || !this.mediaItems.length) return;

        const mediaId = this.openedBy?.getAttribute('data-media-id');
        const activeMedia =
          this.mediaItems.find((element) => element.getAttribute('data-media-id') === mediaId) || this.mediaItems[0];
        const activeMediaTemplate = activeMedia.querySelector('template');
        const activeMediaContent = activeMediaTemplate ? activeMediaTemplate.content : null;

        this.setActiveMedia(activeMedia, false);

        if (
          activeMedia.nodeName === 'DEFERRED-MEDIA' &&
          activeMediaContent &&
          activeMediaContent.querySelector('.js-youtube')
        ) {
          activeMedia.loadContent();
        }
      }

      changeMedia(direction) {
        if (!this.mediaItems.length) return;

        const nextIndex = (this.currentIndex + direction + this.mediaItems.length) % this.mediaItems.length;
        this.setActiveMedia(this.mediaItems[nextIndex]);
      }

      setActiveMedia(media, animate = true) {
        const mediaIndex = this.mediaItems.indexOf(media);
        if (mediaIndex === -1 || !this.content) return;

        this.resetZoom();
        this.currentIndex = mediaIndex;
        this.mediaItems.forEach((element) => {
          element.classList.toggle('active', element === media);
          element.classList.remove('is-entering');
          element.setAttribute('aria-hidden', element === media ? 'false' : 'true');
        });

        if (media instanceof HTMLImageElement) {
          media.loading = 'eager';
          media.fetchPriority = 'high';
          media.decode?.().catch(() => {});
        }

        if (animate && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
          requestAnimationFrame(() => media.classList.add('is-entering'));
        }

        this.content.dataset.activeIndex = mediaIndex;
        this.updateControls(mediaIndex);
      }

      updateControls(currentIndex = this.currentIndex) {
        if (this.currentPageElement) this.currentPageElement.textContent = currentIndex + 1;
        if (this.totalPagesElement) this.totalPagesElement.textContent = this.mediaItems.length;
        this.previousButton?.removeAttribute('disabled');
        this.nextButton?.removeAttribute('disabled');
      }

      get activeImage() {
        const activeMedia = this.mediaItems[this.currentIndex];
        return activeMedia instanceof HTMLImageElement ? activeMedia : null;
      }

      resetZoom() {
        const image = this.activeImage;
        if (image) {
          image.classList.remove('is-zoomed', 'is-dragging');
          image.style.removeProperty('--product-modal-zoom');
          image.style.removeProperty('--product-modal-pan-x');
          image.style.removeProperty('--product-modal-pan-y');
        }
        this.zoomLevel = 1;
        this.pan = { x: 0, y: 0 };
        this.classList.remove('is-zoomed');
      }

      setZoom(level) {
        const image = this.activeImage;
        if (!image) return;

        this.zoomLevel = Math.min(3, Math.max(1, level));
        if (this.zoomLevel === 1) this.pan = { x: 0, y: 0 };
        this.clampPan();
        this.applyTransform();
      }

      toggleZoom() {
        this.setZoom(this.zoomLevel > 1 ? 1 : 2.2);
      }

      clampPan() {
        const image = this.activeImage;
        if (!image) return;

        const maxX = (image.offsetWidth * (this.zoomLevel - 1)) / 2;
        const maxY = (image.offsetHeight * (this.zoomLevel - 1)) / 2;
        this.pan.x = Math.min(maxX, Math.max(-maxX, this.pan.x));
        this.pan.y = Math.min(maxY, Math.max(-maxY, this.pan.y));
      }

      applyTransform() {
        const image = this.activeImage;
        if (!image) return;

        const isZoomed = this.zoomLevel > 1;
        image.style.setProperty('--product-modal-zoom', this.zoomLevel);
        image.style.setProperty('--product-modal-pan-x', `${this.pan.x}px`);
        image.style.setProperty('--product-modal-pan-y', `${this.pan.y}px`);
        image.classList.toggle('is-zoomed', isZoomed);
        this.classList.toggle('is-zoomed', isZoomed);
      }

      onDoubleClick(event) {
        if (this.ignoreNextDoubleClick) {
          this.ignoreNextDoubleClick = false;
          return;
        }
        if (event.target !== this.activeImage || !this.isPointWithinImageContent(this.activeImage, event.clientX, event.clientY)) return;
        event.preventDefault();
        this.toggleZoom();
      }

      onWheel(event) {
        if (event.target !== this.activeImage || !this.isPointWithinImageContent(this.activeImage, event.clientX, event.clientY)) return;

        event.preventDefault();
        const direction = event.deltaY < 0 ? 0.2 : -0.2;
        this.setZoom(this.zoomLevel + direction);
      }

      onPointerDown(event) {
        const image = this.activeImage;
        if (!image || event.target !== image || !this.isPointWithinImageContent(image, event.clientX, event.clientY)) return;

        event.stopPropagation();
        this.pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
        image.setPointerCapture?.(event.pointerId);

        if (this.pointers.size === 1) {
          this.dragStart = {
            x: event.clientX,
            y: event.clientY,
            panX: this.pan.x,
            panY: this.pan.y,
          };
          image.classList.toggle('is-dragging', this.zoomLevel > 1);
        } else if (this.pointers.size === 2) {
          const [first, second] = [...this.pointers.values()];
          this.pinchStartDistance = Math.hypot(second.x - first.x, second.y - first.y);
          this.pinchStartZoom = this.zoomLevel;
        }
      }

      onPointerMove(event) {
        this.updateImageCursor(event);
        if (!this.pointers.has(event.pointerId)) return;

        event.stopPropagation();
        this.pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });

        if (this.pointers.size === 2 && this.pinchStartDistance) {
          const [first, second] = [...this.pointers.values()];
          const distance = Math.hypot(second.x - first.x, second.y - first.y);
          this.setZoom(this.pinchStartZoom * (distance / this.pinchStartDistance));
          return;
        }

        if (this.zoomLevel > 1 && this.dragStart) {
          this.pan.x = this.dragStart.panX + event.clientX - this.dragStart.x;
          this.pan.y = this.dragStart.panY + event.clientY - this.dragStart.y;
          this.clampPan();
          this.applyTransform();
        }
      }

      onPointerUp(event) {
        if (!this.content?.contains(event.target)) return;

        event.stopPropagation();
        const pointerStart = this.dragStart;
        const movedX = pointerStart ? event.clientX - pointerStart.x : 0;
        const movedY = pointerStart ? event.clientY - pointerStart.y : 0;
        const movedDistance = Math.hypot(movedX, movedY);
        const image = this.activeImage;

        if (
          image &&
          event.target === image &&
          this.zoomLevel === 1 &&
          Math.abs(movedX) > 55 &&
          Math.abs(movedX) > Math.abs(movedY)
        ) {
          this.changeMedia(movedX > 0 ? -1 : 1);
        } else if (image && event.target === image && event.pointerType === 'touch' && movedDistance < 12) {
          const now = performance.now();
          if (
            this.lastTap &&
            now - this.lastTap.time < 320 &&
            Math.hypot(event.clientX - this.lastTap.x, event.clientY - this.lastTap.y) < 40
          ) {
            this.toggleZoom();
            this.ignoreNextDoubleClick = true;
            window.setTimeout(() => {
              this.ignoreNextDoubleClick = false;
            }, 400);
            this.lastTap = null;
          } else {
            this.lastTap = { time: now, x: event.clientX, y: event.clientY };
          }
        } else if (event.target === this.content && movedDistance < 8) {
          this.hide();
        }

        this.pointers.delete(event.pointerId);
        image?.classList.remove('is-dragging');
        this.dragStart = null;
        if (this.pointers.size < 2) {
          this.pinchStartDistance = null;
          this.pinchStartZoom = null;
        }
      }

      onBackdropClick(event) {
        if (!this.hasAttribute('open')) return;
        if (event.target.closest('[data-product-modal-control]')) return;

        if (this.isEventOnActiveMedia(event)) return;

        this.hide();
      }

      isEventOnActiveMedia(event) {
        const activeMedia = this.mediaItems[this.currentIndex];
        if (!activeMedia?.contains(event.target)) return false;
        if (!(activeMedia instanceof HTMLImageElement)) return true;

        return this.isPointWithinImageContent(activeMedia, event.clientX, event.clientY);
      }

      isPointWithinImageContent(image, clientX, clientY) {
        if (!image || !image.naturalWidth || !image.naturalHeight) return true;

        const bounds = image.getBoundingClientRect();
        if (!bounds.width || !bounds.height) return false;

        const imageRatio = image.naturalWidth / image.naturalHeight;
        const boxRatio = bounds.width / bounds.height;
        const renderedWidth = imageRatio > boxRatio ? bounds.width : bounds.height * imageRatio;
        const renderedHeight = imageRatio > boxRatio ? bounds.width / imageRatio : bounds.height;
        const left = bounds.left + (bounds.width - renderedWidth) / 2;
        const top = bounds.top + (bounds.height - renderedHeight) / 2;

        return (
          clientX >= left &&
          clientX <= left + renderedWidth &&
          clientY >= top &&
          clientY <= top + renderedHeight
        );
      }

      updateImageCursor(event) {
        const image = this.activeImage;
        if (!image || this.zoomLevel > 1) return;

        const isOverImage =
          event.target === image && this.isPointWithinImageContent(image, event.clientX, event.clientY);
        image.classList.toggle('is-pointer-over-media', isOverImage);
      }

      onKeydown(event) {
        if (!this.hasAttribute('open')) return;
        if (event.key === 'ArrowLeft') {
          event.preventDefault();
          this.changeMedia(-1);
        }
        if (event.key === 'ArrowRight') {
          event.preventDefault();
          this.changeMedia(1);
        }
        if (event.key === '+' || event.key === '=') {
          event.preventDefault();
          this.setZoom(this.zoomLevel + 0.25);
        }
        if (event.key === '-') {
          event.preventDefault();
          this.setZoom(this.zoomLevel - 0.25);
        }
        if (event.key === '0') {
          event.preventDefault();
          this.setZoom(1);
        }
      }
    }
  );
}
